<script setup lang="ts">
import { ref } from 'vue';
import VideoSlideshow from '@/components/shared/VideoSlideshow.vue';
import ImageSlideshow from '@/components/shared/ImageSlideshow.vue';
import DocumentSlideshow from '@/components/shared/DocumentSlideshow.vue';
import i18n from '@/utils/i18n';
import { useGalleriesStore } from '@/stores/galleries';
import { IProfile } from '@/interfaces';
import AuthFlow from '@/components/shared/AuthFlow.vue';

const galleriesStore = useGalleriesStore();

interface Props {
  items: any;
  selectedItemIndex: number;
  mediaType: string;
  allowArchiveRemove?: boolean;
}

withDefaults(defineProps<Props>(), { allowArchiveRemove: false });

const emit = defineEmits(['close-preview']);
const showAuthFlow = ref<boolean>(false);

const archivingStatus = ref<boolean>(true);
const selectedResourceId = ref<string>();
const pinOverlayTitle = ref<string>('');
const successOverlayTitle = ref<string>('');

const archiveAdd = (data: any, textKey: string) => {
  pinOverlayTitle.value = `${i18n.global.t('media.addToMy.' + textKey)}`;
  successOverlayTitle.value = i18n.global.t('media.addedToMy.' + textKey);
  archivingStatus.value = true;
  selectedResourceId.value = data.id;

  showAuthFlow.value = true;
};

const archiveRemove = (data: any, textKey: string) => {
  pinOverlayTitle.value = `${i18n.global.t('media.removeFromMy.' + textKey)}`;
  successOverlayTitle.value = i18n.global.t('media.removedFromMy.' + textKey);
  archivingStatus.value = false;
  selectedResourceId.value = data.id;

  showAuthFlow.value = true;
};

const archive = async (profile: IProfile, pin: string) => {
  try {
    const archiveData = {
      resourceId: selectedResourceId.value,
      profile: {
        profileId: profile.id,
        profileType: profile.type,
        pin,
      },
      participating: archivingStatus.value || false,
    };

    await galleriesStore.archiveAddScreen(archiveData);
  } catch (error) {
    console.error('Archive failed:', error);
  }
};
</script>

<template>
  <Teleport to="body">
    <div class="fixed z-[999]">
      <transition
        enter-active-class="animate__animated animate__fadeIn"
        leave-active-class="animate__animated animate__fadeOut"
        :duration="800"
      >
        <div
          v-if="mediaType !== null"
          class="media-preview h-screen w-full fixed flex flex-col justify-center"
        >
          <VideoSlideshow
            v-if="mediaType === 'videos'"
            :allowArchiveRemove="allowArchiveRemove"
            :videos="items"
            :selectedItemIndex="selectedItemIndex"
            @archive-add="archiveAdd($event, 'videos')"
            @archive-remove="archiveRemove($event, 'videos')"
            @close-preview="emit('close-preview')"
          />

          <ImageSlideshow
            v-if="mediaType === 'pictures' || mediaType === 'galleryPictures'"
            :pictures="items"
            :allowArchiveRemove="allowArchiveRemove"
            :selectedItemIndex="selectedItemIndex"
            @archive-add="archiveAdd($event, 'pictures')"
            @archive-remove="archiveRemove($event, 'pictures')"
            @close-preview="emit('close-preview', mediaType)"
          />

          <DocumentSlideshow
            v-if="mediaType === 'documents'"
            :documents="items"
            :allowArchiveRemove="allowArchiveRemove"
            :selectedItemIndex="selectedItemIndex"
            @close-preview="emit('close-preview')"
            @archive-add="archiveAdd($event, 'documents')"
            @archive-remove="archiveRemove($event, 'documents')"
          />
        </div>
      </transition>

      <AuthFlow
        v-if="showAuthFlow"
        :participating="archivingStatus"
        :title-success="successOverlayTitle"
        :title="pinOverlayTitle"
        @close="showAuthFlow = false"
        @success="(profile, pin) => archive(profile, pin)"
      />
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.media-preview {
  top: 0;
  background-color: rgba(21, 21, 21, 0.9);
}
</style>
